import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { assign } from 'lodash';
import { AlertMessageService } from 'app/core/common/alert.message.service';
import { ReportsService } from 'app/modules/admin/transactions/inwards/reports.service';
import { SiteMeasurementsService } from '../site-measurement.service';
import { SiteMeasurement } from '../site-measurement.types';
import { CommonDropDown, SingleCommonDropDown } from 'app/core/common/types/dropdown.types';
import { LookupService } from 'app/core/common/services/lookup.service';

@Component({
    selector: 'site-measurement-details',
    templateUrl: './site-measurement-details.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteMeasurementDetailsComponent implements OnInit, OnDestroy {

    siteMeasurement: SiteMeasurement;
    siteMeasurementForm: FormGroup;
    dialogTitle = `New Site Measurement`
    alert: any;
    reportType: string;
    selectedType: string
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    total: number;
    options: CommonDropDown[];
    filteredOptions: Observable<CommonDropDown[]>;
    @ViewChild('particularInput', { static: false }) particularInput: ElementRef;

    siteMeasurementFilterForm: FormGroup;
    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) private _data: {
            siteMeasurement: SiteMeasurement,
            reportType: string,
            selectedType: string
        },
        private siteMeasurementService: SiteMeasurementsService,
        private _matDialogRef: MatDialogRef<SiteMeasurementDetailsComponent>,
        private _formBuilder: FormBuilder,
        private _fuseConfirmationService: FuseConfirmationService,
        private _alertMessage: AlertMessageService,
        private _lookupService: LookupService
    ) {
        this.reportType = this._data.reportType;
        this.selectedType = this._data.selectedType;;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Open the drawer
        this.siteMeasurementForm = this._formBuilder.group({
            id: [''],
            reportId: [this._data.siteMeasurement.reportId],
            type: ['Land Area', [Validators.required]],
            particular: [''],
            width: [this._data.siteMeasurement.width],
            depth: [this._data.siteMeasurement.depth],
            total: [this._data.siteMeasurement.total],
            orderIndex: [this._data.siteMeasurement.orderIndex],
        });

        this._lookupService.getOptionByTypeId(91, this.reportType).subscribe((res) => {
            this.options = res;
            this.filteredOptions = this.siteMeasurementForm.get("particular")
                .valueChanges.pipe(
                    startWith(''),
                    map(value => this._optionsfilter(value || '')),
                );
        })

        if (this._data.siteMeasurement.id) {
            // Request the data from the server
            this.siteMeasurementService.getSiteMeasurement(this._data.siteMeasurement.id)
                .subscribe((res) => {
                    if (res.isSuccess) {
                        this.siteMeasurement = res.result;
                        this.dialogTitle = `Edit Site Measurement`;
                        this.siteMeasurementForm.patchValue(this.siteMeasurement);
                    }
                    else {
                        this._alertMessage.error(res.errors.join('\n'));
                        this.onClose();
                    }
                });
        }

        this.siteMeasurementForm.valueChanges
            .pipe(
                debounceTime(500),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((value) => {
                if (value.width && value.depth) {
                    const totalValue = value.width * value.depth;
                    this.siteMeasurementForm.get('total').patchValue(Number(totalValue.toFixed(2)));
                }
            });

        // .subscribe((value) => {
        //     if (value.width && value.depth)
        //         this.siteMeasurementForm.get('total').patchValue(value.width * value.depth);
        // });
        this.defaultRadioValue();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Close the drawer
     */
    // closeDrawer(): Promise<MatDrawerToggleResult> {
    //     return this._optionTypesListComponent.matDrawer.close();
    // }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    setResetForm(oldData: SiteMeasurement) {
        this._data.siteMeasurement as SiteMeasurement;
        this._data.siteMeasurement.reportId = oldData.reportId;
        this._data.siteMeasurement.orderIndex = oldData.orderIndex + 1;
        this._data.siteMeasurement.type = oldData.type;
        this.siteMeasurementForm.reset();
        this.siteMeasurementForm.patchValue(this._data.siteMeasurement);
    }
    saveAndNew() {
        const siteMeasurement = this.siteMeasurementForm.getRawValue();
        this.save(siteMeasurement, true);
        setTimeout(() => this.particularInput.nativeElement.focus(), 0);
    }
    saveAndClose() {
        const siteMeasurement = this.siteMeasurementForm.getRawValue();

        this.save(siteMeasurement);
    }
    save(siteMeasurement, isOpen = false) {
        this.siteMeasurementService.createSiteMeasurement(siteMeasurement).subscribe((response) => {
            if (response.isSuccess) {
                // Show a success message
                this._alertMessage.success("Site measurement added successfully");
                // Close the dialog
                if (!isOpen) {
                    this._matDialogRef.close(response);
                }
                else {
                    this.setResetForm(siteMeasurement);
                }
            }
            else {
                this._alertMessage.error(response.errors.join("\n"));
            }
        });
    }

    updateAndClose() {

        const siteMeasurement = this.siteMeasurementForm.getRawValue();

        this.siteMeasurementService.updateSiteMeasurement(siteMeasurement).subscribe((response) => {
            if (response.isSuccess) {
                // Show a success message
                this._alertMessage.success("Site Measurement updated successfully");
                // Close the dialog
                this._matDialogRef.close(response);
            }
            else {
                this._alertMessage.error(response.errors.join("\n"));
            }
        });

    }

    onClose(): void {
        this._matDialogRef.close();
    }

    displayOptionsFn(options: CommonDropDown[]): (name: string) => string {
        return (name: string) => {
            const correspondingOption = Array.isArray(options) ? options.find(option => option.name === name) : null;
            return correspondingOption ? correspondingOption.name : '';
        }
    }

    private _optionsfilter(name: string): CommonDropDown[] {
        const filterValue = name.toString().toLowerCase();

        return this.options.filter(option => option.name.toLowerCase().includes(filterValue));
    }
    defaultRadioValue() {
        if (this.selectedType == 'Land' || this.selectedType == 'Builtup') {
            this.siteMeasurementForm.controls['type'].setValue('Land Area');

        }
        else if (this.selectedType == 'BuiltUp') {
            this.siteMeasurementForm.controls['type'].setValue('Builtup Area');
        }
        else {
            this.siteMeasurementForm.controls['type'].setValue('Carpet Area');

        }
    }
}
